import { FC, useEffect, useState } from 'react';
import { Grid, TextField, InputAdornment, Button, Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { TestSubmit } from './TestSubmit/TestSubmit';
import AccountCircle from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import { useSendSmsInviteMutation } from '@services/customer-signup/sms';
import { useAppDispatch } from '@hooks/redux-custom';
import { showToast } from '@store/slices/notification';
import { SmsResponse } from '@models';
import { useSendEmailInviteMutation } from '@services/customer-signup/email';

type Props = {
  onClose?: () => void;
};

export interface NewCustomerInviteInputs {
  name: string;
  email: string;
  mobile: string;
}

const fields = {
  name: yup
    .string()
    .required()
    .matches(/^(?![\s.]+$)[a-zA-Z\s.]*$/i, 'Alphabetical characters only'),
  email: yup.string().email(),
  mobile: yup.string().matches(/^\+614[0-9]{8}$/, 'Please enter a valid Australian number starting with +61'),
};

const schema = yup.object().shape(fields);

const CustomerInviteForm: FC<Props> = ({ onClose }) => {
  const [fieldSchema, setFieldSchema] = useState(schema);
  const [inviteOptionValue, setInviteOptionValue] = useState('Email and SMS');

  const inviteOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInviteOptionValue(event.target.value);

    updateFieldSchema(event.target.value);
  };

  const updateFieldSchema = (selectedTab = '') => {
    switch (selectedTab) {
      case 'Email':
        setFieldSchema(yup.object().shape({ ...fields, email: yup.string().email().required(), mobile: yup.string().notRequired() }));
        break;
      case 'SMS':
        setFieldSchema(
          yup.object().shape({
            ...fields,
            mobile: yup
              .string()
              .required()
              .matches(/^\+614[0-9]{8}$/, 'Please enter a valid Australian number starting with +61'),
            email: yup.string().email().notRequired(),
          }),
        );

        break;
      default:
        setFieldSchema(
          yup.object().shape({
            ...fields,
            email: yup.string().email().required(),
            mobile: yup
              .string()
              .required()
              .matches(/^\+614[0-9]{8}$/, 'Please enter a valid Australian number starting with +61'),
          }),
        );

        break;
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<NewCustomerInviteInputs>({
    resolver: yupResolver(fieldSchema),
  });

  console.log('watch variable email', watch('email'));

  const [sendSmsInvite] = useSendSmsInviteMutation();
  const [sendEmailInvite] = useSendEmailInviteMutation();

  const dispatch = useAppDispatch();

  // To send Invite to phone number
  const sendSms = async (input: NewCustomerInviteInputs) => {
    const { mobile, name } = input;

    const response: any = await sendSmsInvite({ toPhone: mobile, name: name });

    if (response?.data?.code === 200) {
      return true;
    }

    return false;
  };

  const sendEmail = async (input: NewCustomerInviteInputs) => {
    const { email, name } = input;

    const response: any = await sendEmailInvite({ toEmail: email, name: name });

    TestSubmit.testEmailSendResponse(true);

    if (response?.data?.code === 200) {
      return true;
    }

    return false;
  };

  //To render toast based on response
  const toastRenderer = (isSuccess: boolean) => {
    TestSubmit.testToastRendered(isSuccess);

    if (isSuccess) {
      dispatch(
        showToast({
          message: `Invite sent successfully!`,
          type: 'success',
        }),
      );
    } else {
      dispatch(
        showToast({
          message: `Failed to send invite!`,
          type: 'error',
        }),
      );
    }
  };

  const formSubmitHandler: SubmitHandler<NewCustomerInviteInputs> = async (data: NewCustomerInviteInputs) => {
    await TestSubmit.testSubmit(data);

    switch (inviteOptionValue?.toLowerCase()) {
      case 'sms':
        TestSubmit.checkBoxSelected('sms');
        toastRenderer(await sendSms(data));
        break;
      case 'email and sms':
        TestSubmit.checkBoxSelected('email and sms');
        toastRenderer((await sendSms(data)) && (await sendEmail(data)));
        break;
      case 'email':
        TestSubmit.checkBoxSelected('email');
        toastRenderer(await sendEmail(data));
        break;

      default: {
        TestSubmit.checkBoxSelected('failed');
        toastRenderer(false);
      }
    }
  };

  useEffect(() => {
    updateFieldSchema();
  }, []);

  return (
    <form onSubmit={handleSubmit(formSubmitHandler)} data-testid="customer-invite-form">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl>
            <FormLabel id="invite-group-label">Invitation Options</FormLabel>
            <RadioGroup
              row
              name="emailAndSmsInvite"
              aria-labelledby="invite-group-label"
              value={inviteOptionValue}
              onChange={inviteOptionChange}
            >
              <FormControlLabel control={<Radio />} label="Email and SMS" value="Email and SMS" data-testid="radio-button-both" />
              <FormControlLabel control={<Radio />} label="Email only" value="Email" data-testid="radio-button-email" />
              <FormControlLabel control={<Radio />} label="SMS only" value="SMS" data-testid="radio-button-sms" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="name"
                label="Name"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : ''}
                fullWidth
              />
            )}
          />
        </Grid>
        {inviteOptionValue !== 'SMS' && (
          <Grid item xs={12}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="email"
                  type="email"
                  label="Email"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
                  fullWidth
                />
              )}
            />
          </Grid>
        )}
        {inviteOptionValue !== 'Email' && (
          <Grid item xs={12}>
            <Controller
              name="mobile"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="mobile"
                  label="Mobile"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SendToMobileIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.mobile}
                  helperText={errors.mobile ? errors.mobile.message : ''}
                  fullWidth
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose} sx={{ display: 'block' }}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" sx={{ marginLeft: 2 }} data-testid="customer-send-invitation-btn">
              Send Invitation
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default CustomerInviteForm;
